import shpwrite from "@mapbox/shp-write";
import { USA_BOUNDING_BOX } from "contexts/MapViewConfigurationContext/hooks/useBoundingBox";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import {
    convertQueryResultAndSplitIntoScopedAndUnauthorizedBuses,
    useBusesWithCapacityCostsVariables
} from "contexts/MapViewDataContext/hooks/useBusData";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useScreeningViewContext } from "contexts/ScreeningViewContext/ScreeningViewContext";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { saveAs } from "file-saver";
import { useGetBusesWithCapacityCostLazyQuery } from "generated/graphql";
import JSZip from "jszip";
import JSZipUtils from "jszip-utils";
import {
    ExportColorType,
    FAIR_COLOR,
    FAIR_COLOR_COLORBLIND,
    getCapacityColorString,
    getConstraintCountColorString,
    GOOD_COLOR,
    GOOD_COLOR_COLORBLIND,
    GREEN_EXPORT_COLOR,
    isHighAvailableCapacityBus,
    isLowConstraintCountBus,
    POOR_COLOR,
    POOR_COLOR_COLORBLIND,
    RED_EXPORT_COLOR,
    YELLOW_EXPORT_COLOR
} from "sharedStyles/capacityColorStyles";
import {
    BusFilters,
    ColorMode,
    DEFAULT_CONSTRAINT_COUNT_THRESHOLD
} from "types/busFilterTypes";
import {
    encryptBusId,
    isLowConfidenceLocationBus,
    ScopedBus
} from "types/busType";
import {
    ComponentConfig,
    isPricedConstraintConfig
} from "types/componentConfigPerRegion";
import { GeneratorType, STORAGE_TYPE } from "types/generatorType";
import {
    getUrlForScreeningViewWithBus,
    ScreeningView
} from "types/screeningViewType";
import greenDot from "./NiraGreenDot.png";
import redDot from "./NiraRedDot.png";
import yellowDot from "./NiraYellowDot.png";

export const KMZ_TYPE = "kmz";
export const SHAPEFILE_TYPE = "shp";
export const EXPORT_TYPES = [KMZ_TYPE, SHAPEFILE_TYPE] as const;
export type ExportType = (typeof EXPORT_TYPES)[number];

export const EXPORT_FILTER_ALL = "ALL";
export const EXPORT_FILTER_HIGH_CAPACITY = "HIGH_AVAILABLE_CAPACITY";
export const EXPORT_FILTER_TYPES = [
    EXPORT_FILTER_ALL,
    EXPORT_FILTER_HIGH_CAPACITY
] as const;
export type ExportFilterType = (typeof EXPORT_FILTER_TYPES)[number];

const KML_HEADER = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>
`;

const KML_FOOTER = `    </Document>
</kml>
`;

const NIRA_RED_DOT = "NiraRedDot";
const NIRA_YELLOW_DOT = "NiraYellowDot";
const NIRA_GREEN_DOT = "NiraGreenDot";
const ALL_DOT_STYLE_IDS = [
    NIRA_RED_DOT,
    NIRA_YELLOW_DOT,
    NIRA_GREEN_DOT
] as const;
type DotStyleId = (typeof ALL_DOT_STYLE_IDS)[number];

const getPngName = (styleId: DotStyleId) => `${styleId}.png`;

const convertDotStyleIdToDotStyleXml = (
    styleId: DotStyleId
) => `       <Style id="${styleId}">
            <IconStyle>
                <scale>0.5</scale>
                <Icon>
                    <href>${getPngName(styleId)}</href>
                </Icon>
            </IconStyle>
        </Style>
`;

const NIRA_DOT_STYLES = ALL_DOT_STYLE_IDS.map(
    convertDotStyleIdToDotStyleXml
).join("");

const capacityColorToStyleId = (capacityColor: ExportColorType): DotStyleId => {
    switch (capacityColor) {
        case RED_EXPORT_COLOR:
            return NIRA_RED_DOT;
        case YELLOW_EXPORT_COLOR:
            return NIRA_YELLOW_DOT;
        case GREEN_EXPORT_COLOR:
            return NIRA_GREEN_DOT;
    }
};

const styleIdToDotImage = (styleId: DotStyleId) => {
    switch (styleId) {
        case NIRA_RED_DOT:
            return redDot;
        case NIRA_YELLOW_DOT:
            return yellowDot;
        case NIRA_GREEN_DOT:
            return greenDot;
    }
};

const zipDotImage = async (zip: JSZip, styleId: DotStyleId) => {
    const dotImageData = await JSZipUtils.getBinaryContent(
        styleIdToDotImage(styleId)
    );
    zip.file(getPngName(styleId), dotImageData, { binary: true });
};

const saveBlob = (
    blob: Blob,
    screeningViewData: ScreeningView,
    extension: "kmz" | "zip"
) => {
    const options: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "short",
        year: "numeric"
    };
    const currentDate = new Date().toLocaleDateString("en-US", options);
    const fileName = `${screeningViewData.title} - ${currentDate}.${extension}`;
    saveAs(blob, fileName);
};

const ONE_MILLION = 1_000_000;

enum ExportKeys {
    BUS_NAME = "Bus Name",
    MW_ENERGY = "Energy (MW)",
    MW_CAPACITY = "Capacity (MW)",
    MW_CHARGING = "Charging (MW)",
    ALLOCATED_COST = "Allocated Cost ($M)",
    TOTAL_COST = "Total Cost ($M)",
    VOLTAGE = "Voltage (kV)",
    OWNER = "Owner",
    COORDINATES = "Coordinates",
    LOCATION_CONFIDENCE = "Location",
    URL = "url",
    COLOR = "color"
}

type ExportBusColumn = {
    key: ExportKeys;
    keyForShapefile: string; // necessary due to the 10 char limit for keys in Shapefiles
    renderValue: (scopedBus: ScopedBus) => string;
    hideColumn?: (
        componentConfig: ComponentConfig,
        generator: GeneratorType
    ) => boolean;
};

const EXPORT_BUS_SHARED_COLUMNS: ExportBusColumn[] = [
    {
        key: ExportKeys.BUS_NAME,
        keyForShapefile: "bus_name",
        renderValue: (scopedBus) => `${scopedBus.bus.busDisplayName}`
    },
    {
        key: ExportKeys.MW_ENERGY,
        keyForShapefile: "energy",
        renderValue: (scopedBus) =>
            `${scopedBus.scopedCapacityEnergyCost.energySize}`
    },
    {
        key: ExportKeys.MW_CAPACITY,
        keyForShapefile: "capacity",
        renderValue: (scopedBus) =>
            `${scopedBus.scopedCapacityEnergyCost.capacitySize}`,
        hideColumn: (componentConfig) =>
            !isPricedConstraintConfig(componentConfig) ||
            !componentConfig.showCapacity
    },
    {
        key: ExportKeys.MW_CHARGING,
        keyForShapefile: "charging",
        renderValue: (scopedBus) =>
            `${scopedBus.scopedCapacityEnergyCost.chargingSize}`,
        hideColumn: (componentConfig, generator) =>
            generator !== STORAGE_TYPE || !componentConfig.showChargingSize
    },
    {
        key: ExportKeys.ALLOCATED_COST,
        keyForShapefile: "alloc_cost",
        renderValue: (scopedBus) =>
            `${(
                scopedBus.scopedCapacityEnergyCost.allocatedCosts / ONE_MILLION
            ).toFixed(1)}`,
        hideColumn: (componentConfig) =>
            !isPricedConstraintConfig(componentConfig) ||
            !componentConfig.hasAllocatedCost
    },
    {
        key: ExportKeys.TOTAL_COST,
        keyForShapefile: "total_cost",
        renderValue: (scopedBus) =>
            `${(
                scopedBus.scopedCapacityEnergyCost.totalCosts / ONE_MILLION
            ).toFixed(1)}`,
        hideColumn: (componentConfig) =>
            !isPricedConstraintConfig(componentConfig)
    },
    {
        key: ExportKeys.VOLTAGE,
        keyForShapefile: "voltage",
        renderValue: (scopedBus) => `${scopedBus.bus.voltage}`
    },
    {
        key: ExportKeys.OWNER,
        keyForShapefile: "owner",
        renderValue: (scopedBus) => scopedBus.bus.ownerName
    },
    {
        key: ExportKeys.COORDINATES,
        keyForShapefile: "coords",
        renderValue: (scopedBus) =>
            `(${scopedBus.bus.latitude.toFixed(
                3
            )}, ${scopedBus.bus.longitude.toFixed(3)})`
    },
    {
        key: ExportKeys.LOCATION_CONFIDENCE,
        keyForShapefile: "location",
        renderValue: (scopedBus) =>
            `${
                isLowConfidenceLocationBus(scopedBus.bus)
                    ? "Low Confidence"
                    : "High Confidence"
            }`
    }
];

const getNiraUrl = (
    scopedBus: ScopedBus,
    screeningViewData: ScreeningView
): string => {
    return getUrlForScreeningViewWithBus(
        screeningViewData,
        encryptBusId(scopedBus.bus.id),
        scopedBus.bus.latitude,
        scopedBus.bus.longitude,
        window.location.origin
    );
};

const escapeXml = (str: string): string =>
    str
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");

const renderExtendedDataEntry = (key: string, value: string) => {
    return `            <Data name="${escapeXml(key)}"><value>${escapeXml(
        value
    )}</value></Data>\n`;
};

const exportColorToHex = (
    color: ExportColorType,
    isColorblindMode: boolean
): string => {
    switch (color) {
        case RED_EXPORT_COLOR:
            return isColorblindMode ? POOR_COLOR_COLORBLIND : POOR_COLOR;
        case YELLOW_EXPORT_COLOR:
            return isColorblindMode ? FAIR_COLOR_COLORBLIND : FAIR_COLOR;
        case GREEN_EXPORT_COLOR:
            return isColorblindMode ? GOOD_COLOR_COLORBLIND : GOOD_COLOR;
    }
};

const exportBusesToKmz = async (
    scopedBuses: readonly ScopedBus[],
    screeningViewData: ScreeningView,
    busFilters: BusFilters,
    generator: GeneratorType,
    componentConfig: ComponentConfig,
    isColorblindMode: boolean
): Promise<void> => {
    const isConstraintCountMode =
        busFilters.colorMode === ColorMode.CONSTRAINT_COUNT;

    const placemarks = scopedBuses
        .map((scopedBus) => {
            const {
                bus: { latitude, longitude },
                scopedCapacityEnergyCost: {
                    energySize,
                    capacitySize,
                    chargingSize
                }
            } = scopedBus;
            const powerAmounts = { energySize, capacitySize, chargingSize };

            const capacityColor = isConstraintCountMode
                ? getConstraintCountColorString(
                      scopedBus.zeroMwConstraintCount ?? 0,
                      busFilters.constraintCountThresholds ??
                          DEFAULT_CONSTRAINT_COUNT_THRESHOLD,
                      isColorblindMode
                  )
                : getCapacityColorString(
                      powerAmounts,
                      busFilters.capacityThresholds,
                      generator,
                      isColorblindMode
                  );

            const dataEntries = EXPORT_BUS_SHARED_COLUMNS.map((column) => {
                const hideColumn =
                    column.hideColumn &&
                    column.hideColumn(componentConfig, generator);

                return hideColumn
                    ? ""
                    : renderExtendedDataEntry(
                          column.key,
                          column.renderValue(scopedBus)
                      );
            });
            dataEntries.push(
                renderExtendedDataEntry(
                    ExportKeys.URL,
                    getNiraUrl(scopedBus, screeningViewData)
                )
            );

            const placemark = `        <Placemark>
            <ExtendedData>
${dataEntries.join("")}            </ExtendedData>
            <styleUrl>${capacityColorToStyleId(capacityColor)}</styleUrl>
            <Point>
                <coordinates>${longitude},${latitude}</coordinates>
            </Point>
        </Placemark>
`;

            return placemark;
        })
        .join("");
    const kml = KML_HEADER + NIRA_DOT_STYLES + placemarks + KML_FOOTER;

    const zip = new JSZip();
    zip.file(`${screeningViewData.title}.kml`, kml);

    for (const styleId of ALL_DOT_STYLE_IDS) {
        await zipDotImage(zip, styleId);
    }

    const fileContent = await zip.generateAsync({
        type: "blob",
        mimeType: "application/vnd.google-earth.kmz"
    });

    saveBlob(fileContent, screeningViewData, "kmz");
};

const exportBusesToShapefile = async (
    scopedBuses: readonly ScopedBus[],
    screeningViewData: ScreeningView,
    busFilters: BusFilters,
    generator: GeneratorType,
    componentConfig: ComponentConfig,
    isColorblindMode: boolean
): Promise<void> => {
    const isConstraintCountMode =
        busFilters.colorMode === ColorMode.CONSTRAINT_COUNT;

    const features = scopedBuses.map((scopedBus) => {
        const {
            bus: { latitude, longitude },
            scopedCapacityEnergyCost: { energySize, capacitySize, chargingSize }
        } = scopedBus;
        const powerAmounts = { energySize, capacitySize, chargingSize };

        const capacityColor = isConstraintCountMode
            ? getConstraintCountColorString(
                  scopedBus.zeroMwConstraintCount ?? 0,
                  busFilters.constraintCountThresholds ??
                      DEFAULT_CONSTRAINT_COUNT_THRESHOLD,
                  isColorblindMode
              )
            : getCapacityColorString(
                  powerAmounts,
                  busFilters.capacityThresholds,
                  generator,
                  isColorblindMode
              );

        const properties = EXPORT_BUS_SHARED_COLUMNS.reduce(
            (propertiesAcc: { [key: string]: string }, column) => {
                if (
                    !column.hideColumn ||
                    !column.hideColumn(componentConfig, generator)
                ) {
                    propertiesAcc[column.keyForShapefile] =
                        column.renderValue(scopedBus);
                }
                return propertiesAcc;
            },
            {}
        );
        properties[ExportKeys.COLOR] = exportColorToHex(
            capacityColor,
            isColorblindMode
        );
        properties[ExportKeys.URL] = getNiraUrl(scopedBus, screeningViewData);

        const feature: GeoJSON.Feature = {
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: [longitude, latitude]
            },
            properties: properties
        };

        return feature;
    });
    const featureCollection: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: features
    };

    const fileContent = (await shpwrite.zip(featureCollection, {
        outputType: "blob",
        compression: "STORE"
    })) as Blob;

    saveBlob(fileContent, screeningViewData, "zip");
};

export const useExportScreeningView: (
    format: ExportType
) => (filter: ExportFilterType) => Promise<void> = (format) => {
    const [getBusDataForScreeningViewExport] =
        useGetBusesWithCapacityCostLazyQuery();
    const maybeScreeningViewData = useScreeningViewContext();
    const exportQueryVariables = useBusesWithCapacityCostsVariables(
        USA_BOUNDING_BOX,
        maybeScreeningViewData?.voltages
    );
    const { generator, componentConfig } = useMapViewRoutingMetadata();
    const {
        busFiltersConfiguration: { busFilters }
    } = useMapViewConfiguration();
    const { isColorblindMode } = useUserDataContext();

    const exportFunc =
        format === KMZ_TYPE ? exportBusesToKmz : exportBusesToShapefile;

    const exportScreeningView = async (filter: ExportFilterType) => {
        if (!maybeScreeningViewData) {
            throw Error("No screening view defined");
        }

        const queryResult = await getBusDataForScreeningViewExport({
            variables: exportQueryVariables,
            fetchPolicy: "no-cache"
        });

        const [scopedBuses] =
            convertQueryResultAndSplitIntoScopedAndUnauthorizedBuses(
                queryResult
            );

        let filteredBuses = scopedBuses;
        if (filter == EXPORT_FILTER_HIGH_CAPACITY) {
            const isConstraintCountMode =
                busFilters.colorMode === ColorMode.CONSTRAINT_COUNT;

            if (isConstraintCountMode) {
                filteredBuses = scopedBuses.filter((scopedBus: ScopedBus) =>
                    isLowConstraintCountBus({
                        constraintCount: scopedBus.zeroMwConstraintCount ?? 0,
                        constraintCountThresholds:
                            busFilters.constraintCountThresholds ??
                            DEFAULT_CONSTRAINT_COUNT_THRESHOLD
                    })
                );
            } else {
                filteredBuses = scopedBuses.filter((scopedBus: ScopedBus) =>
                    isHighAvailableCapacityBus({
                        powerAmounts: scopedBus.scopedCapacityEnergyCost,
                        capacityThresholds: busFilters.capacityThresholds,
                        generatorType: generator
                    })
                );
            }
        }

        await exportFunc(
            filteredBuses,
            maybeScreeningViewData,
            busFilters,
            generator,
            componentConfig,
            isColorblindMode
        );
    };

    return exportScreeningView;
};
